<script setup lang="ts">
import {computed, onMounted} from 'vue';
import i18n from '@/localization/i18n';

import BusinessRequestProgressBar from "./BusinessRequestProgressBar.vue";
import CustomInput from '@/views/components/CustomInput.vue';
import CustomPhoneInput from '@/views/components/CustomPhoneInput.vue';
import Link from '@/views/components/Link.vue'
import VueTurnstile from 'vue-turnstile'
import {storeToRefs} from "pinia";
import {useBusinessRequestFormStore} from "@/pages/index/stores/business-request-form-store.ts";
import {schemaValidator} from "@/pages/index/validation/businessRequestSignUpSchema"
import CustomChecker from '@/views/components/CustomChecker.vue';

const businessRequestFormStore = useBusinessRequestFormStore()
const {signUpFormData, signUpFormErrors, sign_up_loading} = storeToRefs(businessRequestFormStore)
const turnstile_site_key = import.meta.env.VITE_TURNSTILE_SITE_KEY

const shouldEnableSubmitButton = computed(() => {
    try {
        schemaValidator(false).validateSync(signUpFormData.value, { abortEarly: false })
        return true
    } catch (error) {
        return false
    }
})

const t = i18n.global.t

onMounted(() => {
})
</script>

<template>
    <div class="rounded main fullHeight">
        <BusinessRequestProgressBar />
        <div class="fullRemainingHeight">
            <div class="expanded">
                <div class="h-style h3-style use-poppins text-center mt-2">
                    {{ t("w_txt_business_request_signup_heading") }}
                </div>
                <div class="mt-3 mx-md-5">
                    <p class="p-style">
                        {{ t("w_txt_business_request_signup_paragraph") }}
                    </p>
                </div>
                <div class="row">
                    <form @submit.prevent class="col-12 col-sm-12 mb-2 border-end-light">
                        <div class="row">
                            <div class="col-12 col-md-6 mb-2 custom-input-label">
                                <CustomInput
                                    type="text"
                                    id="first_name"
                                    maxlength="150"
                                    v-model:value="signUpFormData.first_name"
                                    :errors="signUpFormErrors.first_name"
                                    :input-label="t('g_frm_firstName') + '*'" />
                            </div>
                            <div class="col-12 col-md-6 mb-2 custom-input-label">
                                <CustomInput
                                    type="text"
                                    id="last_name"
                                    maxlength="150"
                                    v-model:value="signUpFormData.last_name"
                                    :errors="signUpFormErrors.last_name"
                                    :input-label="t('g_frm_lastName') + '*'" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 col-md-6 mb-2 custom-input-label">
                                <CustomInput
                                    type="email"
                                    maxlength="254"
                                    id="new_email"
                                    v-model:value="signUpFormData.email"
                                    :errors="signUpFormErrors.email"
                                    :input-label="t('Email*')" />
                            </div>
                            <div class="col-12 col-md-6 mb-2 custom-input-label">
                                <CustomPhoneInput
                                    type="text"
                                    id="signup_phone"
                                    maxlength="10"
                                    v-model:value="signUpFormData.phone"
                                    :errors="signUpFormErrors.phone"
                                    :input-label="t('g_frm_phone') + '*'" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 d-flex justify-content-end mb-2">
                                <a
                                    href="#"
                                    @click="() => { businessRequestFormStore.next_step('login', false) }"
                                    class="signUpLink text-end"
                                    id="loginLink">
                                    {{ t('w_txt_business_request_signup_account_exists') }}
                                </a>
                            </div>
                        </div>
                        <div class="row justify-content-center">
                            <div class="col-md-8">
                              <vue-turnstile :site-key="turnstile_site_key" v-model="signUpFormData.cloudflare_captcha_token" />
                            </div>
                        </div>
                        <div class="row">
                          <div class="col-12 mt-3">
                            <div class="d-flex flex-row align-items-center mt-1">
                              <div class="form-check form-switch form-check-primary p-0 me-1">
                                <CustomChecker  v-model:value="signUpFormData.terms" type="checkbox" class="form-check-input" id="customSwitch11"
                                />
                                <!-- <label class="form-check-label" for="customSwitch11">
                                    <span class="switch-icon-left"><i data-feather="check"></i></span>
                                    <span class="switch-icon-right"><i data-feather="x"></i></span>
                                </label> -->
                              </div>
                              <label class="form-check-label sized-text" for="customSwitch11" style="">
                                {{ t('w_frm_signup_confirmReading') }} <Link href="/general-conditions-of-use/" class="signUpLink" target="_blank">{{ t("w_txt_home_footerTermsAndConditionsOfUse") }}</Link> {{ t("w_frm_signup_confirmReadingAnd") }} <Link href="/privacy-policy/" class="signUpLink" target="_blank">{{ t('w_act_footer_privacyPolicy') }}</Link>
                              </label>
                            </div>
                            <p class="mb-0 text-danger" v-if="signUpFormErrors.terms">{{ signUpFormErrors.terms }}</p>
                          </div>
                        </div>
                    </form>
                </div>
            </div>
            <div style="margin-top: auto; margin-bottom:10px ">
                <button @click="businessRequestFormStore.prev_step('keywords')"  type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-2 mb-2 custom-btn-width">
                    {{ t("w_act_business_request_previous") }}
                </button>

                <button
                    @click="businessRequestFormStore.signUpUser"
                    :disabled="!shouldEnableSubmitButton"
                    type="button"
                    class="btn btn-primary mt-2 mb-2 on-action-btn custom-btn-width"
                    :class="{ 'invalid-btn-style': !shouldEnableSubmitButton}"
                >
                    <span v-if="!sign_up_loading">{{ t("w_act_business_request_post") }}</span>
                    <span v-else class="spinner-border spinner-border-sm text-light" role="status">
                      <span class="sr-only">Loading...</span>
                    </span>
                </button>
            </div>
        </div>

    </div>
</template>

<style scoped>
.btn-primary:focus{
    background-color: #FEB23C !important;
}
.custom-input-label {
    text-align: left;
    justify-content: flex-start;
}
.main {
    padding-top: 15px;
    position: relative;
}

.bg {
    background-color: #FEB23C !important;
}

.fullHeight {
    height: 100%;
    z-index: 1;
    border: none !important;
    display: flex;                  
    flex-direction: column;
}

.fullRemainingHeight {
    display: flex;                  
    flex-direction: column; 
    height: 100%;  
}

.expanded{
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.custom-btn-width {
    min-width: 130px;
}

/* Medium devices (md) - 768px and up */
@media (min-width: 768px) {
    /* Styles for medium screens */
    .expanded{
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    .custom-btn-width {
        min-width: 150px !important;
    }
}

.signUpLink {
  color: #FEB23C !important;
  text-decoration: none !important;
}

.invalid {
  background-color: rgb(179, 175, 175) !important;
  border-color: rgb(179, 175, 175) !important;
  color: #efefef !important;
}

</style>
